import { useCallback, useMemo, useSyncExternalStore } from "react"

import type { ChatSessionQueryScope, ChatStore } from "./chat-request-registry"

import {
  getChatSessionQueryScopeSnapshot,
  parseChatSessionQueryScope,
  subscribeChatRequestRegistry,
} from "./chat-request-registry"

// Generation 0, never disposed: the query identity of a chat outside any store.
const UNSCOPED_SNAPSHOT = "0:0:0"
const noopUnsubscribe = () => undefined

/**
 * The query identity of `sessionId` in `store`, re-rendering when the chat is
 * deleted or revived (#3524). Without a store or session id the chat keeps its
 * original, unscoped keys.
 */
export function useChatSessionQueryScope(
  store: ChatStore | undefined,
  sessionId: string | undefined
): ChatSessionQueryScope {
  const subscribe = useCallback(
    (listener: () => void) =>
      store ? subscribeChatRequestRegistry(store, listener) : noopUnsubscribe,
    [store]
  )
  const getSnapshot = useCallback(
    () =>
      store && sessionId ? getChatSessionQueryScopeSnapshot(store, sessionId) : UNSCOPED_SNAPSHOT,
    [store, sessionId]
  )
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return useMemo(() => parseChatSessionQueryScope(snapshot), [snapshot])
}
